import { CircularProgress } from '@atgfr8/atg-ui-tailwind';
import { BULK_SHARED_CAPACITY_LIMIT } from '@store/loadboard/bulkSharedCapacity';
import type { ILoadBoardViewModel } from '@store/loadboard/types';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { createEffect, For, Show } from 'solid-js';

type RowTone = 'pending' | 'checking' | 'eligible' | 'ineligible' | 'failed';

export type ShareCapacityRowTone = RowTone;

type RowProps = {
  load: ILoadBoardViewModel;
  checked: boolean;
  disabled: boolean;
  detail: string;
  tone: RowTone;
  onToggle: (checked: boolean) => void;
};

const ShareCapacityLoadRow = (props: RowProps) => {
  return (
    <div
      data-load-id={props.load.loadId}
      class="flex items-start gap-3 rounded px-2 py-2"
      classList={{
        'bg-announcement-feature-surface': props.tone === 'eligible',
        'bg-error-red': props.tone === 'ineligible',
        'bg-announcement-warning-surface': props.tone === 'failed',
      }}
    >
      <div class="mt-1 flex w-4 shrink-0 justify-center">
        <Show when={props.tone === 'eligible'}>
          <input
            type="checkbox"
            class="accent-armstrong-blue"
            checked={props.checked}
            disabled={props.disabled}
            aria-label={`Select load ${props.load.loadNumber}`}
            onChange={(event) => {
              props.onToggle(event.currentTarget.checked);
            }}
          />
        </Show>
      </div>
      <div class="min-w-0 flex-1">
        <div class="text-sm font-semibold text-armstrong-primary">
          {props.load.loadNumber}
        </div>
        <div class="text-sm text-armstrong-primary">
          {props.load.customerName} · {props.load.origin} →{' '}
          {props.load.destination}
        </div>
        <div
          class="mt-1 flex items-center gap-2 text-sm"
          classList={{
            'font-semibold text-announcement-feature':
              props.tone === 'eligible',
            'text-error-red': props.tone === 'ineligible',
            'text-announcement-warning': props.tone === 'failed',
            'text-armstrong-primary':
              props.tone === 'pending' || props.tone === 'checking',
          }}
        >
          <Show when={props.tone === 'checking'}>
            <CircularProgress size={14} />
          </Show>
          <span>{props.detail}</span>
        </div>
      </div>
    </div>
  );
};

export const MassSharedCapacityScrollList = (props: {
  loads: ILoadBoardViewModel[];
  checking: boolean;
  selectedLoadIds: number[];
  eligibleLoadIds: number[];
  allEligibleSelected: boolean;
  ineligibleCount: number;
  detailFor: (loadId: number) => string;
  toneFor: (loadId: number) => RowTone;
  canSelect: (loadId: number) => boolean;
  onToggle: (loadId: number, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
}) => {
  let selectAllElement: HTMLInputElement | undefined;

  createEffect(() => {
    if (selectAllElement === undefined) {
      return;
    }

    const eligibleCount = props.eligibleLoadIds.length;
    const selectedCount = props.selectedLoadIds.length;
    selectAllElement.indeterminate =
      eligibleCount > 0 && selectedCount > 0 && !props.allEligibleSelected;
  });

  return (
    <div class="flex min-h-0 flex-1 flex-col gap-2">
      <div class="flex shrink-0 flex-wrap items-center justify-between gap-2 text-sm">
        <label class="flex items-center gap-2 text-sm text-armstrong-primary">
          <input
            ref={selectAllElement}
            type="checkbox"
            class="accent-armstrong-blue"
            checked={props.allEligibleSelected}
            disabled={props.eligibleLoadIds.length === 0}
            aria-label="Select all eligible loads"
            data-testid={formatDataTestId(
              'Select all eligible loads',
              'checkboxInput',
            )}
            onChange={(event) => {
              props.onSelectAll(event.currentTarget.checked);
            }}
          />
          Select all eligible
        </label>
        <div class="flex flex-wrap gap-2">
          <span class="rounded bg-announcement-feature-surface px-2 py-1 text-announcement-feature">
            Eligible {props.eligibleLoadIds.length}
          </span>
          <span class="rounded bg-error-red px-2 py-1 text-error-red">
            Ineligible {props.ineligibleCount}
          </span>
          <span class="rounded bg-armstrong-light-blue px-2 py-1 text-armstrong-primary">
            Selected {props.selectedLoadIds.length}
          </span>
        </div>
      </div>
      <div class="flex shrink-0 items-center gap-2 text-sm text-armstrong-primary">
        <Show when={props.checking}>
          <CircularProgress size={14} />
        </Show>
        <span>
          {props.checking
            ? `Checking eligibility for ${props.loads.length} loads…`
            : `First ${BULK_SHARED_CAPACITY_LIMIT} loads from the current Loadboard filters.`}
        </span>
      </div>
      <div class="flex min-h-0 flex-1 flex-col gap-1 overflow-auto">
        <Show
          when={props.loads.length > 0}
          fallback={
            <p class="text-sm text-armstrong-primary" role="status">
              No loads match the current Loadboard filters.
            </p>
          }
        >
          <For each={props.loads}>
            {(load) => (
              <ShareCapacityLoadRow
                load={load}
                checked={props.selectedLoadIds.includes(load.loadId)}
                disabled={!props.canSelect(load.loadId)}
                detail={props.detailFor(load.loadId)}
                tone={props.toneFor(load.loadId)}
                onToggle={(checked) => {
                  props.onToggle(load.loadId, checked);
                }}
              />
            )}
          </For>
        </Show>
      </div>
    </div>
  );
};
